'use client'

import { useEffect, useState, useRef } from 'react'

interface TableOfContents {
  id: string
  text: string
  level: number
}

interface ClientHomeContentProps {
  initialContent: string
  fullWidth?: boolean // Thêm prop để điều khiển full width
}

// Function to convert Vietnamese text to slug
function vietnameseToSlug(text: string): string {
  // Bảng chuyển đổi ký tự có dấu sang không dấu
  const vietnameseMap: { [key: string]: string } = {
    'à': 'a', 'á': 'a', 'ạ': 'a', 'ả': 'a', 'ã': 'a', 'â': 'a', 'ầ': 'a', 'ấ': 'a', 'ậ': 'a', 'ẩ': 'a', 'ẫ': 'a', 'ă': 'a', 'ằ': 'a', 'ắ': 'a', 'ặ': 'a', 'ẳ': 'a', 'ẵ': 'a',
    'è': 'e', 'é': 'e', 'ẹ': 'e', 'ẻ': 'e', 'ẽ': 'e', 'ê': 'e', 'ề': 'e', 'ế': 'e', 'ệ': 'e', 'ể': 'e', 'ễ': 'e',
    'ì': 'i', 'í': 'i', 'ị': 'i', 'ỉ': 'i', 'ĩ': 'i',
    'ò': 'o', 'ó': 'o', 'ọ': 'o', 'ỏ': 'o', 'õ': 'o', 'ô': 'o', 'ồ': 'o', 'ố': 'o', 'ộ': 'o', 'ổ': 'o', 'ỗ': 'o', 'ơ': 'o', 'ờ': 'o', 'ớ': 'o', 'ợ': 'o', 'ở': 'o', 'ỡ': 'o',
    'ù': 'u', 'ú': 'u', 'ụ': 'u', 'ủ': 'u', 'ũ': 'u', 'ư': 'u', 'ừ': 'u', 'ứ': 'u', 'ự': 'u', 'ử': 'u', 'ữ': 'u',
    'ỳ': 'y', 'ý': 'y', 'ỵ': 'y', 'ỷ': 'y', 'ỹ': 'y',
    'đ': 'd',
    'À': 'A', 'Á': 'A', 'Ạ': 'A', 'Ả': 'A', 'Ã': 'A', 'Â': 'A', 'Ầ': 'A', 'Ấ': 'A', 'Ậ': 'A', 'Ẩ': 'A', 'Ẫ': 'A', 'Ă': 'A', 'Ằ': 'A', 'Ắ': 'A', 'Ặ': 'A', 'Ẳ': 'A', 'Ẵ': 'A',
    'È': 'E', 'É': 'E', 'Ẹ': 'E', 'Ẻ': 'E', 'Ẽ': 'E', 'Ê': 'E', 'Ề': 'E', 'Ế': 'E', 'Ệ': 'E', 'Ể': 'E', 'Ễ': 'E',
    'Ì': 'I', 'Í': 'I', 'Ị': 'I', 'Ỉ': 'I', 'Ĩ': 'I',
    'Ò': 'O', 'Ó': 'O', 'Ọ': 'O', 'Ỏ': 'O', 'Õ': 'O', 'Ô': 'O', 'Ồ': 'O', 'Ố': 'O', 'Ộ': 'O', 'Ổ': 'O', 'Ỗ': 'O', 'Ơ': 'O', 'Ờ': 'O', 'Ớ': 'O', 'Ợ': 'O', 'Ở': 'O', 'Ỡ': 'O',
    'Ù': 'U', 'Ú': 'U', 'Ụ': 'U', 'Ủ': 'U', 'Ũ': 'U', 'Ư': 'U', 'Ừ': 'U', 'Ứ': 'U', 'Ự': 'U', 'Ử': 'U', 'Ữ': 'U',
    'Ỳ': 'Y', 'Ý': 'Y', 'Ỵ': 'Y', 'Ỷ': 'Y', 'Ỹ': 'Y',
    'Đ': 'D'
  }

  return text
    .trim()
    .split('')
    .map(char => vietnameseMap[char] || char)
    .join('')
    .replace(/[^a-zA-Z0-9\s]/g, '') // Loại bỏ ký tự đặc biệt
    .replace(/\s+/g, '_') // Thay khoảng trắng bằng dấu gạch dưới
    .replace(/_+/g, '_') // Loại bỏ dấu gạch dưới liên tiếp
    .replace(/^_|_$/g, '') // Loại bỏ dấu gạch dưới ở đầu và cuối
}

export default function ClientHomeContent({ initialContent, fullWidth = false }: ClientHomeContentProps) {
  const [toc, setToc] = useState<TableOfContents[]>([])
  const contentRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (contentRef.current) {
      contentRef.current.innerHTML = initialContent
      
      // Chỉ lấy các thẻ H2 cho mục lục
      const headings = contentRef.current.querySelectorAll('h2')
      
      // Không bỏ qua heading đầu tiên nữa, chỉ lọc các heading rỗng
      const newToc = Array.from(headings)
        .filter(heading => {
          const text = heading.textContent?.trim()
          return text && text.length > 0
        })
        .map((heading, index) => {
          const text = heading.textContent?.trim() || ''
          const id = vietnameseToSlug(text) || `section-${index}`
          heading.id = id
          return {
            id,
            text,
            level: 2 // Tất cả đều là level 2 vì chỉ lấy H2
          }
        })
      
      setToc(newToc)
    }
  }, [initialContent])

  const scrollToSection = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault()
    const contentDiv = contentRef.current
    if (!contentDiv) return

    const targetElement = contentDiv.querySelector(`#${id}`)
    if (targetElement) {
      const container = contentDiv
      const elementPosition = targetElement.getBoundingClientRect().top + container.scrollTop - container.getBoundingClientRect().top
      
      container.scrollTo({
        top: elementPosition - 20,
        behavior: 'smooth'
      })
    }
  }

  return (
    <div className="mt-8 w-full flex flex-col lg:flex-row gap-8 min-h-[400px] lg:h-[calc(100vh-200px)] justify-center">
        {/* Mục lục - Chỉ hiển thị trên desktop */}
        <div className="hidden lg:block lg:w-1/4 lg:flex-shrink-0">
          <div className="rounded-lg p-4 h-full shadow-lg border flex flex-col backdrop-blur-xl" style={{ background: 'var(--glass-bg)', borderColor: 'var(--glass-border)' }}>
            <p className="text-lg font-semibold text-white mb-4 pb-2 border-b flex-shrink-0" style={{ borderBottomColor: 'var(--glass-border)' }}>
              Mục lục
            </p>
            <nav 
              className="flex-1 overflow-y-auto pr-2 custom-scrollbar"
              style={{
                scrollbarWidth: 'thin',
                scrollbarColor: 'var(--primary) var(--dark-lighter)'
              }}
            >
              <ul className="space-y-2 list-none">
                {toc.map((item) => (
                  <li key={item.id} style={{ paddingLeft: `${(item.level - 1) * 1}rem` }}>
                    <a
                      href={`#${item.id}`}
                      onClick={(e) => scrollToSection(e, item.id)}
                      className="block text-white/80 hover:text-[var(--primary)] transition-colors duration-200 ease-in-out hover:translate-x-1"
                    >
                      {item.text}
                    </a>
                  </li>
                ))}
              </ul>
            </nav>
          </div>
        </div>

        {/* Nội dung - Full width trên mobile */}
        <div className="w-full lg:flex-1 lg:w-3/4">
          <div 
            ref={contentRef}
            className="prose prose-lg max-w-none prose-invert prose-headings:text-white prose-p:text-gray-300 prose-a:text-[var(--primary)] prose-strong:text-white rounded-lg p-4 lg:p-6 h-auto min-h-[400px] lg:h-full max-h-[70vh] lg:max-h-none overflow-y-auto custom-scrollbar shadow-lg border backdrop-blur-xl"
            style={{
              background: 'var(--glass-bg)',
              borderColor: 'var(--glass-border)',
              scrollbarWidth: 'thin',
              scrollbarColor: 'var(--primary) var(--dark-lighter)'
            }}
          />
        </div>

      <style jsx global>{`
        .custom-scrollbar::-webkit-scrollbar {
          width: 6px;
          height: 6px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-track {
          background: var(--dark-lighter);
          border-radius: 3px;
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb {
          background: var(--primary);
          border-radius: 3px;
          border: 1px solid var(--dark-lighter);
        }
        
        .custom-scrollbar::-webkit-scrollbar-thumb:hover {
          background: #ff8533;
        }

        .prose :where(h1, h2, h3, h4, h5, h6):not(:where([class~="not-prose"] *)) {
          color: var(--primary);
          margin-top: 2em;
          margin-bottom: 1em;
          font-weight: 600;
          scroll-margin-top: 1rem;
        }

        .prose :where(p):not(:where([class~="not-prose"] *)) {
          margin-top: 1.25em;
          margin-bottom: 1.25em;
          line-height: 1.8;
        }

        .prose :where(a):not(:where([class~="not-prose"] *)) {
          text-decoration: none;
          transition: color 0.2s ease-in-out;
        }

        .prose :where(a):not(:where([class~="not-prose"] *)):hover {
          color: #ff8533;
        }

        .prose :where(ul, ol):not(:where([class~="not-prose"] *)) {
          padding-left: 1.5em;
          margin-top: 1.25em;
          margin-bottom: 1.25em;
        }

        .prose :where(li):not(:where([class~="not-prose"] *)) {
          margin-top: 0.5em;
          margin-bottom: 0.5em;
        }

        .prose :where(blockquote):not(:where([class~="not-prose"] *)) {
          border-left: 4px solid var(--primary);
          padding-left: 1em;
          font-style: italic;
          margin: 1.5em 0;
          color: #9ca3af;
        }

        .prose :where(code):not(:where([class~="not-prose"] *)) {
          background: var(--dark-lighter);
          padding: 0.2em 0.4em;
          border-radius: 4px;
          font-size: 0.9em;
        }

        .prose :where(pre):not(:where([class~="not-prose"] *)) {
          background: var(--dark-lighter);
          padding: 1em;
          border-radius: 8px;
          overflow-x: auto;
        }

        /* Mobile typography adjustments */
        @media (max-width: 1024px) {
          .prose {
            font-size: 1rem;
          }
          
          .prose :where(h1):not(:where([class~="not-prose"] *)) {
            font-size: 1.75rem;
          }
          
          .prose :where(h2):not(:where([class~="not-prose"] *)) {
            font-size: 1.5rem;
          }
          
          .prose :where(h3):not(:where([class~="not-prose"] *)) {
            font-size: 1.25rem;
          }
          
          .prose :where(h4, h5, h6):not(:where([class~="not-prose"] *)) {
            font-size: 1.1rem;
          }
        }
      `}</style>
    </div>
  )
} 